import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { lastValueFrom } from 'rxjs';
import { StatusPage, StatusPageComponent, StatusPageIncident } from 'src/app/models/status-page.model';
import { StatusPagesService } from 'src/app/services/health-pages.service';
import { environment } from 'src/environments/environment';

const REFRESH_INTERVAL = 30;
const INCIDENTS_COLLAPSED = 5;

@Component({
  selector: 'app-root',
  templateUrl: './project-status-page.component.html',
  styleUrls: ['./project-status-page.component.scss'],
  standalone: false
})
export class ProjectStatusPageComponent implements OnInit, OnDestroy {
  statusPage: StatusPage | null = null;

  loading = true;
  projectSlug = '';
  lastUpdated: Date | null = null;
  countdown = REFRESH_INTERVAL;
  showAllIncidents = false;

  private _ticker: ReturnType<typeof setInterval> | null = null;
  private _onVisibilityChange = () => {
    if (document.hidden) {
      this._stopTicker();
    } else {
      this._startTicker();
    }
  };

  constructor(
    private statusPageService: StatusPagesService,
    private analyticsService: AnalyticsService,
    private _route: ActivatedRoute,
    private _router: Router,
    private cdr: ChangeDetectorRef,
  ) { }

  async ngOnInit() {
    this.loading = true;
    this.projectSlug = this._route.snapshot.params['projectSlug'];
    await this._loadData(true);
    this.loading = false;
    this._startTicker();
    document.addEventListener('visibilitychange', this._onVisibilityChange);
    this.cdr.markForCheck();
  }

  ngOnDestroy() {
    this._stopTicker();
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
  }

  private async _loadData(trackAnalytics = false) {
    try {
      this.statusPage = await lastValueFrom(
        this.statusPageService.getHealthPage(this.projectSlug)
      );
      this.lastUpdated = new Date();
      if (trackAnalytics) {
        this.analyticsService.trackStatusPageView(this.projectSlug);
      }
    } catch {
      if (trackAnalytics) this.statusPage = null;
      // on background refresh failures, keep showing the last known data
    }
    this.cdr.markForCheck();
  }

  private _startTicker() {
    this._stopTicker();
    this.countdown = REFRESH_INTERVAL;
    this._ticker = setInterval(async () => {
      this.countdown--;
      if (this.countdown <= 0) {
        this.countdown = REFRESH_INTERVAL;
        await this._loadData();
      }
      this.cdr.markForCheck();
    }, 1000);
  }

  private _stopTicker() {
    if (this._ticker !== null) {
      clearInterval(this._ticker);
      this._ticker = null;
    }
  }

  get lastUpdatedLabel(): string {
    if (!this.lastUpdated) return '';
    const seconds = Math.floor((Date.now() - this.lastUpdated.getTime()) / 1000);
    if (seconds < 10) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ago`;
  }

  get logoSrc(): string {
    return `${environment.baseUrl}${this.statusPage.logo_url}`;
  }

  get isPrivateView(): boolean {
    return this._router.url.includes('/main/');
  }

  get publicStatusUrl(): string {
    return `/status/${this.projectSlug}`;
  }

  isInMaintenance(component: StatusPageComponent): boolean {
    return component.is_in_maintenance === true;
  }

  isDown(component: StatusPageComponent): boolean {
    return component.is_down === true && !this.isInMaintenance(component);
  }

  isOperational(component: StatusPageComponent): boolean {
    if (this.isInMaintenance(component)) return true;
    if (component.is_down) return false;
    if (component.averageLast90Days === null) return true;
    return component.averageLast90Days >= 99;
  }

  get overallStatus(): 'ok' | 'maintenance' | 'degraded' | 'down' {
    if (!this.statusPage?.components?.length) return 'ok';
    if (this.statusPage.components.some(c => this.isDown(c))) return 'down';
    if (this.statusPage.components.some(c => !this.isOperational(c))) return 'degraded';
    if (this.statusPage.components.some(c => this.isInMaintenance(c))) return 'maintenance';
    return 'ok';
  }

  get overallHealthy(): boolean {
    return this.overallStatus !== 'degraded' && this.overallStatus !== 'down';
  }

  // -- incident timeline --------------------------------------------------

  get incidents(): StatusPageIncident[] {
    return this.statusPage?.incidents ?? [];
  }

  get visibleIncidents(): StatusPageIncident[] {
    return this.showAllIncidents ? this.incidents : this.incidents.slice(0, INCIDENTS_COLLAPSED);
  }

  get hiddenIncidentCount(): number {
    return this.incidents.length - this.visibleIncidents.length;
  }

  /** "Sep 27" plus its ordinal suffix, as in "Sep 27th". */
  dayOrdinal(value: string): string {
    const day = new Date(value).getDate();
    if (day % 100 >= 11 && day % 100 <= 13) return 'th';
    return ['th', 'st', 'nd', 'rd'][day % 10] ?? 'th';
  }

  sameDay(a: string, b: string): boolean {
    return new Date(a).toDateString() === new Date(b).toDateString();
  }

  duration(incident: StatusPageIncident): string {
    const end = incident.resolved_at ? new Date(incident.resolved_at).getTime() : Date.now();
    const minutes = Math.max(0, Math.round((end - new Date(incident.started_at).getTime()) / 60000));
    if (minutes < 1) return 'less than a minute';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return minutes % 60 ? `${hours} h ${minutes % 60} min` : `${hours} h`;
    const days = Math.floor(hours / 24);
    return hours % 24 ? `${days} d ${hours % 24} h` : `${days} d`;
  }
}
